import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import AdvancedDataTableComponent from '../../common/AdvancedDataTableComponent';
import FloatingLabelSelect from '../../ui/FloatingLabelSelect';
import { fetchSubClientData,  fetchUploadedDataByFD } from '../../../services/client/customerApiService';
import { getallocatedreportByFilters } from '../../../services/client/reportApiService';
import { ProcessingOverlay } from '../../common/ProcessingOverlay';
import AlternateDatePicker2 from '../../ui/AlternateDatePicker2';
import { format } from 'date-fns';


const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;
const getAuthKey = () => {
    return `${process.env.REACT_APP_API_SECURITY_KEY}`;
};
const AllocationReport = ({ setSearchResults, handleTableRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [assigningData, setAssigningData] = useState([]);  // Data for the table
    const [isOpen, setIsOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [templateContent, setTemplateContent] = useState("");
    const [formValues, setFormValues] = useState({
        clientId: '',
        subClientId: '',
        fromDate: null,
        toDate: null
    });
    const [subClients, setSubClients] = useState([]);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate(); // Initialize useNavigate

    const inputClasses = (field) => (errors[field] ? 'border-red-500' : 'border-gray-300');

    useEffect(() => {
        const getSubClients = async () => {
            try {
                const subClientData = await fetchSubClientData(localStorage.getItem('clientID'));
                setSubClients([{ value: -1, label: 'All' }, ...subClientData.map(subClient => ({
                    value: subClient.subClientId,
                    label: subClient.subClientName
                }))]);
            } catch (error) {
                console.error("Error fetching sub-clients:", error);
            }
        };
        getSubClients();
    }, []);

    const validate = () => {
        const newErrors = {};
        if (!formValues.subClientId) newErrors.subClientId = 'Client is required';
        if (!formValues.fromDate) newErrors.fromDate = 'FromDate is required';
        if (!formValues.toDate) newErrors.toDate = 'ToDate is required';
        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length === 0) {
            setLoading(true);
            setIsProcessing(true);
            try {
                // Manually add the clientId here instead of relying on the setFormValues update
                const updatedFormValues = { ...formValues, clientId: localStorage.getItem('clientID') };

                //console.log(updatedFormValues); // You should see the clientId now included here
                const data = await getallocatedreportByFilters(updatedFormValues);
                if (data.success && data.success.length > 0) {
                    setAssigningData(data.success);
                    setApiResponse({ success: 'Files fetched successfully!' });
                } else {
                    setAssigningData([]);
                    setApiResponse({ error: 'No Data Found' });
                }
            } catch (error) {
                console.error("Submission failed:", error);
                setApiResponse({ error: 'Failed to submit form. Please try again.' });
                setAssigningData([]);
                setIsProcessing(false);
            } finally {
                setLoading(false);
                setErrors(validationErrors);
                setIsProcessing(false);
            }
        } else {
            setErrors(validationErrors);
        }
    };

    const resetFormValues = () => {
        setFormValues({
            clientId: '',
            subClientId: '',
            fromDate: null,
            toDate: null
        });
        setErrors({});
    };

    const handleCancel = () => {
        resetFormValues();
        navigate('/allocationReport');
    };
    const toggleDialog = () => {
        setIsOpen(!isOpen);
    };
    const handleShowPopoUpSubmit = async (rowObject) => {
        setIsProcessing(true);
        try {
            const fetchUploadedData = await fetchUploadedDataByFD(rowObject.fileDetailsId);
            if (fetchUploadedData.statusCode === 1001) {
                // Parse JSON string into an object
                const parsedData = JSON.parse(fetchUploadedData.result);

                // Add `fileDetailsId` only if it doesn't exist
                if (!parsedData.hasOwnProperty('fileDetailsId')) {
                    parsedData.fileDetailsId = rowObject.fileDetailsId;
                }

                // Convert back to JSON string
                const updatedResult = JSON.stringify(parsedData);
                setTemplateContent(updatedResult);


                toggleDialog(); // Open the dialog after fetching content
            }
        } catch (error) {
            console.error("Error fetching template:", error);
            setIsProcessing(false);
        } finally {
            setIsProcessing(false);
        }
    }

    const tableHeaders = ["Client Name", "Branch", "Checkin ID", "Customer Name", "Allocated Date", "Component", "TAT (Days)", "Executive"];
    const headerMapping = {
        "Client Name": "subClientName",
        "Branch": "branchName",
        "Checkin ID": "checkinId",
        "Customer Name": "customerName",
        "Allocated Date": "allocatedDate",
        "Component": "componentName",
        "TAT (Days)": "tat",
        "Executive":"fieldOfficerName"
    };

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            <form onSubmit={handleSubmit} className="space-y-6 w-full">
                <h1>File Allocation</h1>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    <div>
                        <FloatingLabelSelect
                            label="Select Client"
                            id="subClientId"
                            name="subClientId"
                            value={subClients.find(subClient => subClient.value === formValues.subClientId) || null}
                            onChange={(option) => setFormValues({ ...formValues, subClientId: option.value })}
                            className={inputClasses('subClientId')}
                            options={subClients}
                            isSearchable={true}
                        />
                         {errors.subClientId && <div className="text-red-500 text-sm">{errors.subClientId}</div>}
                    </div>
                    <div>
                        {/* From Date Picker */}
                        <AlternateDatePicker2
                            label="From Date"
                            id="fromDate"
                            name="fromDate"
                            value={formValues.fromDate ? new Date(formValues.fromDate) : null}
                            onChange={(event) => {
                                const date = event.target.value ? event.target.value : null;
                                setFormValues({ ...formValues, fromDate: date });
                            }}
                            className={inputClasses('fromDate')}
                        />

                        {errors.fromDate && <div className="text-red-500 text-sm">{errors.fromDate}</div>}
                    </div>



                    <div>
                        {/* To Date Picker */}
                        <AlternateDatePicker2
                            label="To Date"
                            id="toDate"
                            name="toDate"
                            value={formValues.toDate ? new Date(formValues.toDate) : null}
                            onChange={(event) => {
                                const date = event.target.value ? event.target.value : null;
                                setFormValues({ ...formValues, toDate: date });
                            }}
                            className={inputClasses('toDate')}
                        />
                        {errors.toDate && <div className="text-red-500 text-sm">{errors.toDate}</div>}
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        type="submit"
                        className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${loading ? 'opacity-50' : ''}`}
                        disabled={loading}
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                </div>

                {apiResponse && (
                    <motion.div
                        className={`mt-4 flex items-center ${apiResponse.error ? 'text-red-500' : 'text-green-500'}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {apiResponse.error ? (
                            <>
                                <FaExclamationCircle className="mr-2" />
                                {apiResponse.error}
                            </>
                        ) : ''
                            // (
                            //     <>
                            //         <FaCheckCircle className="mr-2" />
                            //         {apiResponse.success}
                            //     </>
                            // )
                        }
                    </motion.div>
                )}
            </form>
            <br />
            <hr />
            <br />
            {(assigningData!=null && assigningData.length>0) ? ( 
            <div>
               
            <AdvancedDataTableComponent
                headers={tableHeaders}
                data={assigningData.map((item, index) => ({
                    ...item,
                    popup_show: (

                        <input
                            type="button"
                            value="Show"
                            onClick={() => handleShowPopoUpSubmit(item.fileDetailsId)}
                        />
                    ),
                    allocatedDate: item.allocatedDate
                    ? format(new Date(item.allocatedDate), 'dd-MM-yyyy') // Format the allocatedDate field here
                    : 'N/A', // Handle cases where allocatedDate might be null or undefined
                }))}
                headerMapping={headerMapping}
                extatAction={true}
                extraActionMethod={handleShowPopoUpSubmit}
                exportExcel={true}
            />
            </div>
            ):<div>No Data found</div>}

{isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white w-11/12 max-w-4xl p-6 rounded shadow-lg overflow-auto max-h-screen">
                        {/* Close Button */}
                        <button
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                            onClick={toggleDialog}
                        >
                            ✖
                        </button>

                        {/* Render the template content as a JSON table */}
                        {templateContent && templateContent.length > 0 &&
                            <div>
                                <JsonTable data={templateContent}/></div>}
                    </div>
                </div>
            )}
            {/* Display the overlay when processing */}
            <ProcessingOverlay isProcessing={isProcessing} message="Please wait, we're processing your request..." />

        </div>
    );
};



export default AllocationReport;


const JsonTable = ({ data }) => {
    const [modifiedData, setModifiedData] = useState(data); // Store modified data

    // Check if data is a string and parse it if so
    let parsedData = modifiedData;
    if (typeof parsedData === 'string') {
        try {
            parsedData = JSON.parse(parsedData);
            setModifiedData(parsedData);
        } catch (e) {
            // If parsing fails, show an error message
            return <div className="text-red-500">Error: Invalid JSON string</div>;
        }
    }

    // Check if parsedData is an object
    if (!parsedData || typeof parsedData !== 'object') {
        return <div>No data available</div>;
    }

    // Get the keys of the object to display each key-value pair
    const keys = Object.keys(parsedData);


    return (
        <div className="overflow-x-auto">
            
            <table className="min-w-full table-auto bg-white bg-opacity-30 shadow-lg rounded-lg border border-gray-200">
                <thead className="bg-blue-200 bg-opacity-80">
                    <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Field</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Value</th>
                    </tr>
                </thead>
                <tbody>
                    {keys
                        .filter((key) => key !== "fileDetailsId")
                        .map((key, index) => (
                            <tr
                                key={key}
                                className={`${index % 2 === 0
                                    ? 'bg-blue-50 bg-opacity-70'
                                    : 'bg-green-50 bg-opacity-70'
                                    } hover:bg-blue-100 hover:bg-opacity-70 transition-colors duration-300`}
                            >
                                <td className="px-6 py-3 text-sm text-gray-800 font-medium">{key}</td>
                                <td className="px-6 py-3 text-sm text-gray-600">
                                    
                                       { parsedData[key] }
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>

        </div>
    );
};




